import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  CheckCircle2,
  Copy,
  ExternalLink,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import { SkeletonTable } from '../../components/Skeleton';
import { toast } from '../../lib/toast';
import { formatRelative } from '../../lib/format';

// Admin → Resellers. Super-admin-only. Lets you spin up white-label
// resellers, generate their unique IPN webhook URL, and track customer
// counts. Reseller-owners themselves don't see this page; they manage
// their customers via the existing /admin/users (data-scoped server-side).
export default function ResellersPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ owner_user_id: '', brandName: '', notes: '' });
  const [revealedSlug, setRevealedSlug] = useState(null);

  const resellersQuery = useQuery({
    queryKey: ['admin-resellers'],
    queryFn: () => api.get('/api/admin/resellers').then((r) => r.data.resellers),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/api/admin/resellers', body).then((r) => r.data.reseller),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin-resellers'] });
      setCreating(false);
      setDraft({ owner_user_id: '', brandName: '', notes: '', _ownerEmail: '', _ownerName: '' });
      setRevealedSlug(r.ipnSlug);
      toast.success(`Reseller created — webhook URL ready`);
    },
    onError: (err) => toast.error(err, 'Could not create reseller'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) =>
      api.put(`/api/admin/resellers/${id}`, body).then((r) => r.data.reseller),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-resellers'] });
      setEditingId(null);
      toast.success('Reseller updated');
    },
    onError: (err) => toast.error(err, 'Could not update reseller'),
  });

  const rotateSlugMutation = useMutation({
    mutationFn: (id) =>
      api.post(`/api/admin/resellers/${id}/rotate-slug`).then((r) => r.data.reseller),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin-resellers'] });
      toast.success(`Slug rotated — share the new webhook URL with ${r.brandName}`);
    },
    onError: (err) => toast.error(err, 'Could not rotate slug'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/admin/resellers/${id}`).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin-resellers'] });
      toast.success(
        `Reseller deleted — ${data.detachedCustomers || 0} customers detached`,
      );
    },
    onError: (err) => toast.error(err, 'Could not delete'),
  });

  const rows = resellersQuery.data || [];

  function commitCreate() {
    if (!draft.owner_user_id || !draft.brandName) {
      toast.error('Owner user ID and brand name are required');
      return;
    }
    createMutation.mutate(draft);
  }

  function commitEdit(row) {
    updateMutation.mutate({
      id: row._id,
      body: {
        brandName: row._editBrandName,
        notes: row._editNotes,
        isActive: row.isActive,
        maxCustomers: row._editMaxCustomers,
      },
    });
  }

  return (
    <div className="card">
      <div className="section-header flex-wrap gap-3">
        <div className="section-title">
          <span className="section-icon">
            <Building2 className="w-4 h-4" />
          </span>
          Resellers
          {resellersQuery.data && (
            <span className="text-xs text-ink-400 font-normal ml-1">({rows.length})</span>
          )}
        </div>
        {!creating && (
          <button type="button" className="btn-primary text-xs" onClick={() => setCreating(true)}>
            <Plus className="w-3 h-3" /> Add reseller
          </button>
        )}
      </div>

      <p className="text-sm text-ink-500 mb-3">
        Enterprise customers who sell RankOnAI under their own brand. Each gets a unique
        IPN webhook URL — give it to them to paste into their Launchpad cart. Purchases
        through their URL auto-attach customers to them, and they see those customers in
        the Users page.
      </p>

      {revealedSlug && (
        <SlugRevealBanner slug={revealedSlug} onDismiss={() => setRevealedSlug(null)} />
      )}

      {creating && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <OwnerPicker
              selected={draft.owner_user_id ? draft : null}
              onPick={(u) =>
                setDraft({
                  ...draft,
                  owner_user_id: u._id,
                  _ownerEmail: u.email,
                  _ownerName: u.name || '',
                })
              }
              onClear={() =>
                setDraft({
                  ...draft,
                  owner_user_id: '',
                  _ownerEmail: '',
                  _ownerName: '',
                })
              }
            />
            <label className="text-xs text-ink-600">
              Brand name
              <input
                className="input mt-1"
                value={draft.brandName}
                maxLength={120}
                onChange={(e) => setDraft({ ...draft, brandName: e.target.value })}
              />
            </label>
          </div>
          <label className="text-xs text-ink-600 block">
            Notes (internal)
            <textarea
              className="input mt-1"
              rows={2}
              placeholder="Anything you'd want a future admin to know."
              value={draft.notes}
              maxLength={1000}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setCreating(false);
                setDraft({ owner_user_id: '', brandName: '', notes: '', _ownerEmail: '', _ownerName: '' });
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={createMutation.isPending}
              onClick={commitCreate}
            >
              <Save className="w-3 h-3" /> Create reseller
            </button>
          </div>
        </div>
      )}

      {resellersQuery.isLoading ? (
        <SkeletonTable rows={3} cols={4} wrap={false} />
      ) : rows.length === 0 && !creating ? (
        <EmptyState
          icon={Building2}
          title="No resellers yet"
          body='Click "Add reseller" to create the first one. Resellers manage their own customers via /admin/users.'
        />
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-xs text-ink-500 uppercase tracking-wide">
                <th className="text-left font-medium py-2 px-3">Brand</th>
                <th className="text-left font-medium py-2 px-3">Owner</th>
                <th className="text-left font-medium py-2 px-3">IPN webhook URL</th>
                <th className="text-right font-medium py-2 px-3">Customers</th>
                <th className="text-right font-medium py-2 px-3">Max</th>
                <th className="text-left font-medium py-2 px-3">Status</th>
                <th className="text-right font-medium py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) =>
                editingId === r._id ? (
                  <EditRow
                    key={r._id}
                    row={r}
                    pending={updateMutation.isPending}
                    onSave={(patch) => commitEdit({ ...r, ...patch })}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <Row
                    key={r._id}
                    row={r}
                    onEdit={() => setEditingId(r._id)}
                    onRotateSlug={() => {
                      if (
                        window.confirm(
                          'Rotate this reseller\'s webhook URL? Their existing Launchpad config will break until they update it with the new URL.',
                        )
                      ) {
                        rotateSlugMutation.mutate(r._id);
                      }
                    }}
                    onDelete={() => {
                      if (
                        window.confirm(
                          `Delete reseller "${r.brandName}"? Their customers stay but get detached.`,
                        )
                      ) {
                        deleteMutation.mutate(r._id);
                      }
                    }}
                  />
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ row, onEdit, onRotateSlug, onDelete }) {
  const webhookUrl = buildWebhookUrl(row.ipnSlug);

  return (
    <tr className="hover:bg-gray-50">
      <td className="py-2 px-3">
        {row.brandName?.trim() ? (
          <div className="font-medium text-ink-800">{row.brandName}</div>
        ) : (
          <div className="text-sm text-amber-600 italic">(not set by customer)</div>
        )}
        {row.notes && (
          <div className="text-xs text-ink-400 mt-0.5 truncate max-w-xs">{row.notes}</div>
        )}
      </td>
      <td className="py-2 px-3">
        <div className="text-ink-700">{row.owner_user_id?.email || '—'}</div>
        {row.owner_user_id?.name && (
          <div className="text-xs text-ink-400">{row.owner_user_id.name}</div>
        )}
      </td>
      <td className="py-2 px-3 max-w-md">
        <code className="text-[10px] font-mono text-ink-700 break-all">{webhookUrl}</code>
        <div className="flex items-center gap-2 mt-1">
          <button
            type="button"
            className="btn-ghost text-[10px]"
            onClick={() => {
              navigator.clipboard.writeText(webhookUrl);
              toast.success('Webhook URL copied');
            }}
          >
            <Copy className="w-3 h-3" /> Copy
          </button>
          <button
            type="button"
            className="btn-ghost text-[10px]"
            onClick={onRotateSlug}
            title="Generate a new slug (breaks the current URL)"
          >
            <RefreshCw className="w-3 h-3" /> Rotate
          </button>
        </div>
      </td>
      <td className="py-2 px-3 text-right">
        <span className="inline-flex items-center gap-1 text-ink-700">
          <Users className="w-3.5 h-3.5 text-ink-400" />
          {row.liveCustomerCount ?? row.customerCount ?? 0}
        </span>
      </td>
      <td className="py-2 px-3 text-right text-ink-700">
        {row.maxCustomers === -1 ? (
          <span className="text-xs text-ink-400">∞</span>
        ) : (
          row.maxCustomers
        )}
      </td>
      <td className="py-2 px-3">
        {row.isActive ? (
          <span className="pill bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="w-3 h-3" /> Active
          </span>
        ) : (
          <span className="pill bg-gray-100 text-gray-500">Paused</span>
        )}
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        <button type="button" className="btn-ghost text-xs" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className="btn-ghost text-xs text-rose-600 hover:text-rose-700"
          onClick={onDelete}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

function EditRow({ row, pending, onSave, onCancel }) {
  const [brandName, setBrandName] = useState(row.brandName);
  const [notes, setNotes] = useState(row.notes || '');
  const [isActive, setIsActive] = useState(row.isActive);
  const [maxCustomers, setMaxCustomers] = useState(
    row.maxCustomers === -1 ? '' : String(row.maxCustomers),
  );
  const [unlimited, setUnlimited] = useState(row.maxCustomers === -1);
  return (
    <tr className="bg-amber-50/40">
      <td className="py-2 px-3" colSpan={7}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
          <label className="text-xs text-ink-600">
            Brand name
            <input
              className="input mt-1"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
            />
          </label>
          <label className="text-xs text-ink-600">
            Max customers
            <div className="flex items-center gap-2 mt-1">
              <input
                className="input flex-1"
                type="number"
                min="0"
                disabled={unlimited}
                value={maxCustomers}
                onChange={(e) => setMaxCustomers(e.target.value)}
              />
              <label className="text-[10px] text-ink-600 flex items-center gap-1 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={unlimited}
                  onChange={(e) => setUnlimited(e.target.checked)}
                />
                ∞
              </label>
            </div>
          </label>
          <label className="text-xs text-ink-600 flex items-center gap-2 mt-5">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Active
          </label>
        </div>
        <label className="text-xs text-ink-600 block">
          Notes
          <textarea
            className="input mt-1"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" className="btn-ghost text-xs" onClick={onCancel}>
            <X className="w-3 h-3" /> Cancel
          </button>
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={pending}
            onClick={() =>
              onSave({
                _editBrandName: brandName,
                _editNotes: notes,
                _editMaxCustomers: unlimited ? -1 : Math.max(0, parseInt(maxCustomers || '0', 10)),
                isActive,
              })
            }
          >
            <Save className="w-3 h-3" /> {pending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </td>
    </tr>
  );
}

function SlugRevealBanner({ slug, onDismiss }) {
  const url = buildWebhookUrl(slug);
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-4">
      <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4" />
        Reseller created — give them this webhook URL
      </div>
      <code className="block bg-white border border-emerald-100 rounded-md p-2 text-xs font-mono break-all mb-2">
        {url}
      </code>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={() => {
            navigator.clipboard.writeText(url);
            toast.success('Webhook URL copied');
          }}
        >
          <Copy className="w-3 h-3" /> Copy URL
        </button>
        <button type="button" className="btn-ghost text-xs" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

// Email-driven user picker. Wraps the existing /api/admin/users?q=
// search endpoint with a 300ms debounce. Picking a row stamps the
// _id onto the create draft so the admin never has to copy raw ObjectIds.
function OwnerPicker({ selected, onPick, onClear }) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const searchQuery = useQuery({
    queryKey: ['reseller-owner-search', debounced],
    queryFn: () =>
      api
        .get(`/api/admin/users?q=${encodeURIComponent(debounced)}&limit=8`)
        .then((r) => r.data.users || []),
    enabled: debounced.length >= 2 && !selected,
  });

  if (selected?.owner_user_id) {
    return (
      <label className="text-xs text-ink-600">
        Owner
        <div className="input mt-1 flex items-center justify-between gap-2 bg-white">
          <div className="min-w-0">
            <div className="text-ink-800 truncate">{selected._ownerEmail}</div>
            {selected._ownerName && (
              <div className="text-[10px] text-ink-400 truncate">{selected._ownerName}</div>
            )}
          </div>
          <button
            type="button"
            className="btn-ghost text-[10px] flex-none"
            onClick={onClear}
          >
            <X className="w-3 h-3" /> Change
          </button>
        </div>
        <span className="text-[10px] text-ink-400 mt-1 block font-mono">
          _id: {selected.owner_user_id}
        </span>
      </label>
    );
  }

  return (
    <label className="text-xs text-ink-600 relative">
      Owner
      <div className="relative mt-1">
        <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
        <input
          className="input pl-7"
          placeholder="Search by email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <span className="text-[10px] text-ink-400 mt-1 block">
        Type at least 2 characters of the user's email or name.
      </span>
      {debounced.length >= 2 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {searchQuery.isLoading ? (
            <div className="px-3 py-2 text-xs text-ink-400">Searching…</div>
          ) : (searchQuery.data || []).length === 0 ? (
            <div className="px-3 py-2 text-xs text-ink-400">
              No users match “{debounced}”.
            </div>
          ) : (
            searchQuery.data.map((u) => (
              <button
                key={u._id}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-b-0"
                onClick={() => onPick(u)}
              >
                <div className="text-sm text-ink-800 truncate">{u.email}</div>
                {u.name && (
                  <div className="text-[10px] text-ink-400 truncate">{u.name}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </label>
  );
}

function buildWebhookUrl(slug) {
  // Both segments live in env vars — we don't know LAUNCHPAD_WEBHOOK_SLUG
  // client-side. Show a placeholder for the platform slug so the admin can
  // see the structure; copy will give a hint.
  const base =
    (typeof window !== 'undefined' && window.location.origin) || 'https://YOUR-DOMAIN';
  return `${base}/api/webhooks/launchpad/<LAUNCHPAD_WEBHOOK_SLUG>/${slug}`;
}
